import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  FileUp, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCcw,
  PenTool, Eraser, Trash2, X, BookOpen, Loader2, FolderOpen,
  MousePointer, Search, FileText, Calendar, Check
} from 'lucide-react';
import { Document, Page } from 'react-pdf';
import LiveClassPageStack, { LiveClassAnnotationCanvas, boundedCanvasSize } from './LiveClassPageStack';
import '@/utils/pdfConfig';
import { PDF_CONFIG, PDF_RUNTIME_FLAGS } from '@/utils/pdfConfig';
import axios from 'axios';
import { apiUrl, mediaUrl, API_BASE_URL } from '@/api/config';
import { getAuthToken } from '@/utils/auth';
import { getTeacherNotes } from '@/api/teacherNotes';
import { liveDocumentUrl, documentShareError } from '@/utils/liveDocumentUrls';
import { RoomEvent } from 'livekit-client';
import { Capacitor, CapacitorHttp } from '@capacitor/core';

class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error) {
    console.warn('Caught react-pdf Page render error:', error?.message || error);
    this.props.onError?.(error);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center p-8 bg-white text-slate-400">
          <span className="text-xs font-semibold">Could not render page content</span>
        </div>
      );
    }
    return this.props.children;
  }
}

// Automatically upgrade http:// to https:// when app is served over HTTPS to avoid Mixed Content errors
const ensureHttps = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && url.startsWith('http://')) {
    return url.replace(/^http:\/\//i, 'https://');
  }
  return url;
};

// Helper to identify whether a URL targets our backend API (where JWT headers should be sent)
const isBackendUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  if (url.startsWith('/') && !url.startsWith('//')) return true;
  if (
    url.includes('r2.cloudflarestorage.com') ||
    url.includes('budleestagingstorage.budlee.ai') ||
    url.includes('budleemediastorage.budlee.ai') ||
    url.includes('.r2.dev') ||
    url.includes('amazonaws.com')
  ) {
    return false;
  }
  try {
    const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const backendOrigin = API_BASE_URL && API_BASE_URL.startsWith('http')
      ? new URL(API_BASE_URL).origin
      : (typeof window !== 'undefined' ? window.location.origin : '');
    return parsed.origin === backendOrigin && parsed.pathname.includes('/api/');
  } catch {
    return false;
  }
};

export default function LiveClassDocumentViewer({
  isTutor = true,
  isDark = true,
  room = null,
  activeDocument = null,
  onDocumentChange = () => {},
  onClose = () => {},
  sessionDetails = {},
}) {
  const isTutorUser = Boolean(
    isTutor ||
    (typeof window !== 'undefined' && (window.location.href.includes('/teacher') || window.location.hash.includes('/teacher'))) ||
    Boolean(getAuthToken('teacher'))
  );

  const [docState, setDocState] = useState(() => activeDocument || {
    url: '',
    name: '',
    type: 'image', // 'image' | 'pdf'
    currentPage: 1,
    totalPages: 1,
  });

  const [isUploading, setIsUploading] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [interactionMode, setInteractionMode] = useState('scroll'); // 'scroll' | 'draw'
  const [penMode, setPenMode] = useState('pen'); // 'pen' | 'eraser'
  const [penColor, setPenColor] = useState('#ef4444'); // red default for document markup
  const [penSize, setPenSize] = useState(4);
  const [isDrawing, setIsDrawing] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [retryVersion, setRetryVersion] = useState(0);
  const [pageManifest, setPageManifest] = useState(null);
  const [pageDimensions, setPageDimensions] = useState({});
  const [shareError, setShareError] = useState('');
  const [pdfFileSource, setPdfFileSource] = useState(null);
  const [isPdfFetching, setIsPdfFetching] = useState(false);
  const [containerBounds, setContainerBounds] = useState({ width: 0, height: 0 });
  const createdBlobUrlRef = useRef(null);
  const shareRequestRef = useRef(null);

  useEffect(() => () => {
    shareRequestRef.current?.abort();
    if (createdBlobUrlRef.current) URL.revokeObjectURL(createdBlobUrlRef.current);
  }, []);

  // Library modal states
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [libraryNotes, setLibraryNotes] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [librarySearch, setLibrarySearch] = useState('');
  const [libraryFilter, setLibraryFilter] = useState('all'); // 'all' | 'pdf' | 'image'

  // Per-page canvas and container refs
  const pageCanvasRefs = useRef({});
  const pageContainerRefs = useRef({});
  const containerRef = useRef(null);
  const strokesRef = useRef([]);
  const localLastPosRef = useRef({});
  const remoteLastPosRef = useRef({});
  const activeDrawingPageRef = useRef(null);
  const docStateRef = useRef(docState);
  docStateRef.current = docState;

  // Scroll sync synchronization refs
  const isRemoteScrollRef = useRef(false);
  const lastScrollBroadcastTimeRef = useRef(0);
  const scrollEndTimerRef = useRef(null);

  const authToken = getAuthToken('teacher') || getAuthToken('student') || getAuthToken();

  // Memoize pdfOptions so react-pdf does not recreate worker transport on renders
  const pdfOptions = useMemo(() => ({
    cMapUrl: PDF_CONFIG.cMapUrl,
    cMapPacked: PDF_CONFIG.cMapPacked,
    disableAutoFetch: false,
    disableStream: false,
    disableRange: false,
    withCredentials: false,
    verbosity: 0,
    ...PDF_RUNTIME_FLAGS,
  }), []);

  // Pre-fetch remote PDF into in-memory bytes for seamless native & web rendering
  useEffect(() => {
    if (isUploading || docState.type !== 'pdf' || !docState.url || docState.pageManifestUrl) {
      setPdfFileSource(null);
      return;
    }

    if (docState.url.startsWith('blob:') || docState.url.startsWith('data:')) {
      setPdfFileSource(docState.url);
      setPdfError(false);
      return;
    }

    let isMounted = true;
    const controller = new AbortController();
    setPdfFileSource(null);
    setIsPdfFetching(true);
    setPdfError(false);

    if (createdBlobUrlRef.current) {
      URL.revokeObjectURL(createdBlobUrlRef.current);
      createdBlobUrlRef.current = null;
    }

    const fetchPdfBytes = async () => {
      try {
        const targetUrl = ensureHttps(docState.url);
        const token = authToken;
        const isBackend = isBackendUrl(targetUrl);
        const headers = (isBackend && token) ? { Authorization: `Bearer ${token}` } : {};

        let uint8Array = null;

        if (Capacitor.isNativePlatform()) {
          try {
            const response = await CapacitorHttp.get({
              url: targetUrl,
              responseType: 'arraybuffer',
              headers,
            });

            if (response?.data) {
              if (typeof response.data === 'string') {
                const binaryString = atob(response.data);
                const len = binaryString.length;
                uint8Array = new Uint8Array(len);
                for (let i = 0; i < len; i++) {
                  uint8Array[i] = binaryString.charCodeAt(i);
                }
              } else if (response.data instanceof ArrayBuffer) {
                uint8Array = new Uint8Array(response.data);
              } else if (response.data instanceof Uint8Array) {
                uint8Array = response.data;
              }
            }
          } catch (nativeErr) {
            console.warn('CapacitorHttp native PDF fetch failed, falling back to axios:', nativeErr);
          }
        }

        if (!uint8Array) {
          const response = await axios.get(targetUrl, {
            responseType: 'arraybuffer',
            headers,
            signal: controller.signal,
          });
          if (response?.data) {
            uint8Array = new Uint8Array(response.data);
          }
        }

        if (!isMounted) return;

        if (uint8Array && uint8Array.length > 0) {
          setPdfFileSource({ data: uint8Array });
        } else {
          setPdfFileSource(ensureHttps(docState.url));
        }
      } catch (err) {
        console.warn('PDF pre-fetch failed, falling back to direct URL:', err);
        if (!isMounted) return;
        setPdfFileSource(ensureHttps(docState.url));
      } finally {
        if (isMounted) {
          setIsPdfFetching(false);
        }
      }
    };

    fetchPdfBytes();

    return () => {
      isMounted = false;
      controller.abort();
      if (createdBlobUrlRef.current) {
        URL.revokeObjectURL(createdBlobUrlRef.current);
        createdBlobUrlRef.current = null;
      }
    };
  }, [docState.url, docState.type, docState.pageManifestUrl, authToken, retryVersion, isUploading]);

  useEffect(() => {
    setPageDimensions({});
  }, [docState.url]);

  useEffect(() => {
    if (!docState.pageManifestUrl) { setPageManifest(null); return undefined; }
    const controller = new AbortController();
    setPdfError(false);
    setIsPdfFetching(true);
    const manifestUrl = docState.pageManifestUrl;
    axios.get(ensureHttps(manifestUrl), { signal: controller.signal }).then(({ data }) => {
      if (controller.signal.aborted) return;
      if (!Array.isArray(data.pages) || !data.pages.length || data.pages.length > 500) {
        throw new Error('Invalid page manifest');
      }
      setPageManifest({ url: manifestUrl, pages: data.pages });
      const updated = { ...docStateRef.current, totalPages: data.pages.length };
      setDocState(updated);
      onDocumentChange(updated);
    }).catch((error) => {
      if (!controller.signal.aborted) setPdfError(documentShareError(error, 'prepare'));
    }).finally(() => {
      if (!controller.signal.aborted) setIsPdfFetching(false);
    });
    return () => controller.abort();
  }, [docState.pageManifestUrl, retryVersion]);

  const displayPages = useMemo(() => {
    if (docState.pageManifestUrl) return pageManifest?.url === docState.pageManifestUrl ? pageManifest.pages : [];
    return Array.from({ length: Math.min(500, Math.max(1, docState.totalPages || 1)) }, (_, index) =>
      pageDimensions[index + 1] || { width: 595, height: 842 });
  }, [docState.pageManifestUrl, docState.totalPages, pageManifest, pageDimensions]);

  // Compute responsive rendering width for each PDF page
  const pageRenderWidth = useMemo(() => {
    const baseW = containerBounds.width > 0 ? containerBounds.width : 800;
    const fitW = Math.max(280, Math.min(baseW - 40, 1100));
    return Math.round(fitW * (zoom > 1 ? zoom : 1));
  }, [containerBounds.width, zoom]);

  // Sync external document prop changes
  useEffect(() => {
    if (activeDocument) {
      setDocState({
        ...activeDocument,
        url: ensureHttps(activeDocument.url),
      });
      setPdfError(false);
    }
  }, [activeDocument]);

  // Dynamically measure middle scroll container bounds
  useEffect(() => {
    if (!containerRef.current) return;
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerBounds({ width: Math.round(rect.width), height: Math.round(rect.height) });
      }
    };
    updateSize();
    const ro = new ResizeObserver(updateSize);
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Broadcast data channel messages helper
  const sendDocMessage = useCallback((msg) => {
    if (!room?.localParticipant) return;
    try {
      const packet = {
        ...msg,
        sender: room.localParticipant.identity || 'user',
      };
      const payload = JSON.stringify(packet);
      const bytes = new TextEncoder().encode(payload);
      if (bytes.length > 60000) {
        console.warn(`Doc message payload too large (${bytes.length} bytes). Skipping publish.`);
        return;
      }
      room.localParticipant.publishData(bytes, { reliable: true });
    } catch (e) {
      console.warn('Failed to publish doc message over data channel:', e);
    }
  }, [room]);

  // Scroll synchronization: broadcast scroll position when tutor scrolls
  const handleContainerScroll = () => {
    if (!isTutorUser || !containerRef.current || isRemoteScrollRef.current) return;
    const el = containerRef.current;
    const maxScroll = el.scrollHeight - el.clientHeight;
    if (maxScroll <= 0) return;
    const ratio = el.scrollTop / maxScroll;

    const now = Date.now();
    if (now - lastScrollBroadcastTimeRef.current > 40) {
      lastScrollBroadcastTimeRef.current = now;
      sendDocMessage({
        type: 'DOC_SCROLL_SYNC',
        ratio,
        scrollTop: el.scrollTop,
      });
    }

    if (scrollEndTimerRef.current) clearTimeout(scrollEndTimerRef.current);
    scrollEndTimerRef.current = setTimeout(() => {
      if (containerRef.current && !isRemoteScrollRef.current) {
        const curEl = containerRef.current;
        const curMax = curEl.scrollHeight - curEl.clientHeight;
        if (curMax > 0) {
          sendDocMessage({
            type: 'DOC_SCROLL_SYNC',
            ratio: curEl.scrollTop / curMax,
            scrollTop: curEl.scrollTop,
          });
        }
      }
    }, 120);
  };

  // Clears all canvas markings
  const clearLocalCanvas = useCallback(() => {
    Object.values(pageCanvasRefs.current).forEach((canvas) => {
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx?.clearRect(0, 0, canvas.width, canvas.height);
      }
    });
    strokesRef.current = [];
    localLastPosRef.current = {};
    remoteLastPosRef.current = {};
  }, []);

  // Replays recorded strokes on a specific page canvas
  const redrawPageCanvasStrokes = useCallback((targetPage) => {
    const canvas = pageCanvasRefs.current[targetPage];
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    let lastPos = null;
    (strokesRef.current || []).forEach((stroke) => {
      const strokePage = Number(stroke.page || 1);
      if (strokePage !== Number(targetPage)) return;

      const isStart = stroke.type === 'start' || stroke.type === 'DOC_DRAW_START';
      const isMove = stroke.type === 'move' || stroke.type === 'DOC_DRAW_MOVE';
      const strokeX = stroke.x <= 1 ? stroke.x * canvas.width : stroke.x;
      const strokeY = stroke.y <= 1 ? stroke.y * canvas.height : stroke.y;
      const strokeColor = stroke.color || '#ef4444';
      const strokeSize = stroke.size || 2;
      const isEraser = stroke.mode === 'eraser';

      if (isStart) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(strokeX, strokeY, (isEraser ? 36 : strokeSize) / 2, 0, Math.PI * 2);
        ctx.fillStyle = isEraser ? 'rgba(0,0,0,1)' : strokeColor;
        ctx.globalCompositeOperation = isEraser ? 'destination-out' : 'source-over';
        ctx.fill();
        ctx.restore();
        lastPos = { x: strokeX, y: strokeY };
      } else if (isMove) {
        if (lastPos) {
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(lastPos.x, lastPos.y);
          ctx.lineTo(strokeX, strokeY);
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.strokeStyle = isEraser ? 'rgba(0,0,0,1)' : strokeColor;
          ctx.lineWidth = isEraser ? 36 : strokeSize;
          ctx.globalCompositeOperation = isEraser ? 'destination-out' : 'source-over';
          ctx.stroke();
          ctx.restore();
        }
        lastPos = { x: strokeX, y: strokeY };
      } else if (stroke.type === 'end' || stroke.type === 'DOC_DRAW_END') {
        lastPos = null;
      }
    });
  }, []);

  const redrawAllPageCanvasStrokes = useCallback(() => {
    Object.keys(pageCanvasRefs.current).forEach((pageNum) => {
      redrawPageCanvasStrokes(Number(pageNum));
    });
  }, [redrawPageCanvasStrokes]);

  // Ensure high-resolution canvas size matching page viewport
  const ensurePageCanvasResolution = useCallback((pageNum, w, h) => {
    const canvas = pageCanvasRefs.current[pageNum];
    if (!canvas) return;
    const { width: targetW, height: targetH } = boundedCanvasSize(w, h);

    if (canvas.width !== targetW || Math.abs(canvas.height - targetH) > 5) {
      canvas.width = targetW;
      canvas.height = targetH;
    }
    redrawPageCanvasStrokes(pageNum);
  }, [redrawPageCanvasStrokes]);

  const registerCanvas = useCallback((pageNum, canvas) => {
    if (canvas) {
      pageCanvasRefs.current[pageNum] = canvas;
      redrawPageCanvasStrokes(pageNum);
    } else {
      delete pageCanvasRefs.current[pageNum];
      delete localLastPosRef.current[pageNum];
      delete remoteLastPosRef.current[pageNum];
    }
  }, [redrawPageCanvasStrokes]);

  const renderOverlay = (pageNum, width, height) => (
    <LiveClassAnnotationCanvas
      pageNumber={pageNum} pageWidth={width} pageHeight={height} register={registerCanvas}
      onMouseDown={(e) => startDraw(e, pageNum)}
      onMouseMove={(e) => drawMove(e, pageNum)}
      onMouseUp={() => endDraw(pageNum)} onMouseLeave={() => endDraw(pageNum)}
      onTouchStart={(e) => startDraw(e, pageNum)}
      onTouchMove={(e) => drawMove(e, pageNum)} onTouchEnd={() => endDraw(pageNum)}
      className={`absolute inset-0 w-full h-full block z-10 ${isTutorUser && interactionMode === 'draw'
        ? 'cursor-crosshair pointer-events-auto' : 'pointer-events-none'}`}
      style={{ touchAction: interactionMode === 'draw' ? 'none' : 'auto' }}
    />
  );

  // Core packet handler: processes document and drawing events from WebRTC data channel or CustomEvent
  const handleDocumentPacket = useCallback((data) => {
    if (!data?.type) return;

    if (data.type === 'DOC_LOAD' && data.doc) {
      const cleanDoc = {
        ...data.doc,
        url: ensureHttps(data.doc.url),
      };
      setDocState(cleanDoc);
      onDocumentChange(cleanDoc);
      setPdfError(false);
      clearLocalCanvas();
    } else if (data.type === 'DOC_REQUEST_SYNC') {
      if (isTutorUser && docStateRef.current?.url) {
        sendDocMessage({
          type: 'DOC_LOAD',
          doc: docStateRef.current,
        });
        if (strokesRef.current?.length > 0) {
          sendDocMessage({
            type: 'DOC_STROKES_SYNC',
            strokes: strokesRef.current,
          });
        }
      }
    } else if (data.type === 'DOC_STROKES_SYNC') {
      if (Array.isArray(data.strokes)) {
        strokesRef.current = data.strokes;
        redrawAllPageCanvasStrokes();
      }
    } else if (data.type === 'DOC_PAGE_CHANGE') {
      const targetPage = Number(data.page || 1);
      setDocState((prev) => ({ ...prev, currentPage: targetPage }));
      const targetEl = pageContainerRefs.current[targetPage];
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else if (data.type === 'DOC_SCROLL_SYNC') {
      const el = containerRef.current;
      if (!el) return;
      isRemoteScrollRef.current = true;
      const maxScroll = el.scrollHeight - el.clientHeight;
      if (maxScroll > 0 && typeof data.ratio === 'number') {
        el.scrollTop = Math.round(data.ratio * maxScroll);
      } else if (typeof data.scrollTop === 'number') {
        el.scrollTop = data.scrollTop;
      }
      setTimeout(() => {
        isRemoteScrollRef.current = false;
      }, 100);
    } else if (data.type === 'DOC_SYNC_PAGES') {
      setDocState((prev) => ({ ...prev, totalPages: data.totalPages }));
    } else if (data.type === 'DOC_DRAW_START' || data.type === 'start') {
      const pageNum = Number(data.page || 1);
      const strokeColor = data.color || '#ef4444';
      const strokeSize = Number(data.size || 2);
      const isEraser = data.mode === 'eraser';

      // Always save to strokesRef.current so drawings are NEVER lost even while canvas is mounting
      strokesRef.current.push({
        type: 'start',
        page: pageNum,
        x: data.x,
        y: data.y,
        color: strokeColor,
        size: strokeSize,
        mode: data.mode,
      });

      const canvas = pageCanvasRefs.current[pageNum];
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const startX = data.x <= 1 ? data.x * canvas.width : data.x;
      const startY = data.y <= 1 ? data.y * canvas.height : data.y;

      remoteLastPosRef.current[pageNum] = { x: startX, y: startY };

      ctx.save();
      ctx.beginPath();
      ctx.arc(startX, startY, (isEraser ? 36 : strokeSize) / 2, 0, Math.PI * 2);
      ctx.fillStyle = isEraser ? 'rgba(0,0,0,1)' : strokeColor;
      ctx.globalCompositeOperation = isEraser ? 'destination-out' : 'source-over';
      ctx.fill();
      ctx.restore();
    } else if (data.type === 'DOC_DRAW_MOVE' || data.type === 'move') {
      const pageNum = Number(data.page || 1);
      const strokeColor = data.color || '#ef4444';
      const strokeSize = Number(data.size || 2);
      const isEraser = data.mode === 'eraser';

      // Always save to strokesRef.current
      strokesRef.current.push({
        type: 'move',
        page: pageNum,
        x: data.x,
        y: data.y,
        color: strokeColor,
        size: strokeSize,
        mode: data.mode,
      });

      const canvas = pageCanvasRefs.current[pageNum];
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const moveX = data.x <= 1 ? data.x * canvas.width : data.x;
      const moveY = data.y <= 1 ? data.y * canvas.height : data.y;
      const lastPos = remoteLastPosRef.current[pageNum] || { x: moveX, y: moveY };

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(lastPos.x, lastPos.y);
      ctx.lineTo(moveX, moveY);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = isEraser ? 'rgba(0,0,0,1)' : strokeColor;
      ctx.lineWidth = isEraser ? 36 : strokeSize;
      ctx.globalCompositeOperation = isEraser ? 'destination-out' : 'source-over';
      ctx.stroke();
      ctx.restore();

      remoteLastPosRef.current[pageNum] = { x: moveX, y: moveY };
    } else if (data.type === 'DOC_DRAW_END' || data.type === 'end') {
      const pageNum = Number(data.page || 1);
      remoteLastPosRef.current[pageNum] = null;
      strokesRef.current.push({ type: 'end', page: pageNum });
    } else if (data.type === 'DOC_DRAW_CLEAR') {
      clearLocalCanvas();
    }
  }, [isTutorUser, room, clearLocalCanvas, redrawAllPageCanvasStrokes, onDocumentChange, sendDocMessage]);

  // Listen to remote real-time drawing and document events on LiveKit data channel
  useEffect(() => {
    if (!room) return;

    const handleDataReceived = (payload, participant) => {
      if (participant?.identity && room.localParticipant?.identity && participant.identity === room.localParticipant.identity) {
        return;
      }
      try {
        let text = '';
        if (typeof payload === 'string') {
          text = payload;
        } else if (payload instanceof Uint8Array || payload instanceof ArrayBuffer) {
          text = new TextDecoder().decode(payload);
        } else if (payload?.buffer) {
          text = new TextDecoder().decode(payload.buffer);
        }
        if (!text) return;
        const data = JSON.parse(text);
        if (data.type && (data.type.startsWith('DOC_') || data.type === 'start' || data.type === 'move' || data.type === 'end')) {
          handleDocumentPacket(data);
        }
      } catch (e) {
        // Non-JSON message, ignore
      }
    };

    room.on(RoomEvent.DataReceived, handleDataReceived);
    return () => {
      room.off(RoomEvent.DataReceived, handleDataReceived);
    };
  }, [room, handleDocumentPacket]);

  // If student is in document viewer but has no document loaded, request current document from tutor
  useEffect(() => {
    if (!isTutorUser && !docState.url && room?.localParticipant) {
      try {
        const payload = JSON.stringify({ type: 'DOC_REQUEST_SYNC', sender: room.localParticipant.identity });
        room.localParticipant.publishData(new TextEncoder().encode(payload), { reliable: true });
      } catch (e) {}
    }
  }, [isTutorUser, docState.url, room]);

  // Load teacher library documents
  const handleOpenLibrary = async () => {
    setShowLibraryModal(true);
    setLoadingLibrary(true);
    try {
      const data = await getTeacherNotes({ is_live_class: 'true' });
      const notes = (data?.notes || []).filter(
        (n) => n.file_url || n.file_path || n.file_upload
      );
      setLibraryNotes(notes);
    } catch (err) {
      console.error('Failed to load teacher library documents:', err);
      setLibraryNotes([]);
    } finally {
      setLoadingLibrary(false);
    }
  };

  // Select document from library
  const handleSelectLibraryDoc = async (note) => {
    if (isUploading) return;
    const rawUrl = note.file_url || note.file_path || note.attachment_url;
    if (!rawUrl) return;
    const fileUrl = ensureHttps(mediaUrl(rawUrl) || rawUrl);

    const isPdf =
      fileUrl.toLowerCase().includes('.pdf') ||
      (note.file_name && note.file_name.toLowerCase().endsWith('.pdf')) ||
      (note.title && note.title.toLowerCase().endsWith('.pdf'));

    let pageManifestUrl;
    shareRequestRef.current?.abort();
    const controller = new AbortController();
    shareRequestRef.current = controller;
    setShareError('');
    if (isPdf) {
      setIsUploading(true);
      try {
        const { data } = await axios.post(apiUrl(`v1/teachers/notes/${note.id}/live-share/`), {}, {
          headers: { Authorization: `Bearer ${getAuthToken('teacher')}` },
          signal: controller.signal,
        });
        if (!data.manifest_url) throw new Error('Missing document pages');
        pageManifestUrl = liveDocumentUrl(data.manifest_url);
      } catch (error) {
        if (!controller.signal.aborted) setShareError(documentShareError(error, 'prepare'));
        return;
      } finally { setIsUploading(false); }
    }
    if (controller.signal.aborted) return;
    const newDoc = {
      pageManifestUrl,
      url: fileUrl,
      name: note.title || note.file_name || 'Class Document',
      type: isPdf ? 'pdf' : 'image',
      currentPage: 1,
      totalPages: 1,
    };

    setDocState(newDoc);
    setPdfError(false);
    setZoom(1);
    onDocumentChange(newDoc);
    clearLocalCanvas();
    setShowLibraryModal(false);

    sendDocMessage({
      type: 'DOC_LOAD',
      doc: newDoc,
    });
  };

  // Handle local device file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    shareRequestRef.current?.abort();
    const controller = new AbortController();
    shareRequestRef.current = controller;

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const localBlobUrl = URL.createObjectURL(file);
    createdBlobUrlRef.current = localBlobUrl;

    const initialDoc = {
      url: localBlobUrl,
      name: file.name,
      type: isPdf ? 'pdf' : 'image',
      currentPage: 1,
      totalPages: 1,
    };

    setDocState(initialDoc);
    setPdfFileSource(null);
    setShareError('');
    setPdfError(false);
    setZoom(1);
    clearLocalCanvas();

    setIsUploading(true);
    let uploadStage = 'upload';
    try {
      let fileUrl = '';
      let uploadedNoteId = null;

      const token = getAuthToken('teacher');
      if (token) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('title', file.name);
        formData.append('visible_to_students', 'true');
        formData.append('allow_download', 'true');
        formData.append('is_live_class', 'true');

        const validChapterId = sessionDetails?.chapterId
          || sessionDetails?.chapter_id
          || sessionDetails?.booking?.chapter_id;

        if (validChapterId && !isNaN(Number(validChapterId))) {
          formData.append('chapter_id', Number(validChapterId));
        }

        try {
          const res = await axios.post(apiUrl('v1/teachers/notes/upload/'), formData, {
            signal: controller.signal,
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          });
          uploadedNoteId = res.data?.id;
          const rawUrl = res.data?.file_url || res.data?.attachment_url || res.data?.url || res.data?.file_path;
          fileUrl = rawUrl ? mediaUrl(rawUrl) || rawUrl : '';
        } catch (uploadErr) {
          throw uploadErr;
        }
      }

      if (!fileUrl || (isPdf && !uploadedNoteId)) {
        throw new Error('Document upload failed');
      }
      let pageManifestUrl;
      if (isPdf) {
        uploadStage = 'prepare';
        const { data } = await axios.post(apiUrl(`v1/teachers/notes/${uploadedNoteId}/live-share/`), {}, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!data.manifest_url) throw new Error('Missing document pages');
        pageManifestUrl = liveDocumentUrl(data.manifest_url);
      }

      if (controller.signal.aborted) return;

      const sharedDoc = {
        pageManifestUrl,
        url: fileUrl,
        name: file.name,
        type: isPdf ? 'pdf' : 'image',
        currentPage: 1,
        totalPages: 1,
      };

      setDocState(sharedDoc);
      onDocumentChange(sharedDoc);

      sendDocMessage({
        type: 'DOC_LOAD',
        doc: sharedDoc,
      });
    } catch (err) {
      if (controller.signal.aborted) return;
      setShareError(documentShareError(err, uploadStage));
      setDocState({ url: '', type: 'image', currentPage: 1, totalPages: 1 });
      console.error('Error loading file:', err);
    } finally {
      setIsUploading(false);
      URL.revokeObjectURL(localBlobUrl);
      if (createdBlobUrlRef.current === localBlobUrl) createdBlobUrlRef.current = null;
    }
  };

  // Change page smoothly
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > docState.totalPages) return;
    const updated = { ...docState, currentPage: newPage };
    setDocState(updated);
    onDocumentChange(updated);

    const targetEl = pageContainerRefs.current[newPage];
    if (targetEl) {
      targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    sendDocMessage({
      type: 'DOC_PAGE_CHANGE',
      page: newPage,
    });
  };

  // react-pdf load success handler
  const handleDocumentLoadSuccess = (pdf) => {
    const numPages = pdf?.numPages || 1;
    setPdfError(false);
    const updated = { ...docStateRef.current, totalPages: numPages };
    setDocState(updated);
    onDocumentChange(updated);
    if (isTutorUser) {
      sendDocMessage({
        type: 'DOC_SYNC_PAGES',
        totalPages: numPages,
      });
    }
  };

  const handlePageLoadSuccess = (page, pageNum) => {
    try {
      const viewport = page.getViewport({ scale: 1.0 });
      if (viewport?.width && viewport?.height) {
        setPageDimensions(prev => prev[pageNum]?.width === viewport.width && prev[pageNum]?.height === viewport.height
          ? prev : { ...prev, [pageNum]: { width: viewport.width, height: viewport.height } });
        const aspect = viewport.height / viewport.width;
        ensurePageCanvasResolution(pageNum, 1600, Math.round(1600 * aspect));
      } else {
        ensurePageCanvasResolution(pageNum, 1600, 2262);
      }
    } catch (err) {
      console.warn('Error reading page viewport:', err);
      ensurePageCanvasResolution(pageNum, 1600, 2262);
    }
  };

  // Canvas drawing handlers for a specific page
  const getCanvasPos = (e, canvas) => {
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches?.[0]?.clientX ?? e.clientX;
    const clientY = e.touches?.[0]?.clientY ?? e.clientY;
    return {
      x: ((clientX - rect.left) / rect.width) * canvas.width,
      y: ((clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const startDraw = (e, pageNum = 1) => {
    if (!isTutorUser || interactionMode !== 'draw') return;
    const canvas = pageCanvasRefs.current[pageNum];
    if (!canvas) return;
    const pos = getCanvasPos(e, canvas);
    if (!pos) return;

    activeDrawingPageRef.current = pageNum;
    setIsDrawing(true);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const strokeColor = penColor;
    const strokeSize = penSize;
    const isEraser = penMode === 'eraser';

    ctx.save();
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, (isEraser ? 36 : strokeSize) / 2, 0, Math.PI * 2);
    ctx.fillStyle = isEraser ? 'rgba(0,0,0,1)' : strokeColor;
    ctx.globalCompositeOperation = isEraser ? 'destination-out' : 'source-over';
    ctx.fill();
    ctx.restore();

    localLastPosRef.current[pageNum] = { x: pos.x, y: pos.y };

    const normalizedX = canvas.width > 0 ? pos.x / canvas.width : pos.x / 1600;
    const normalizedY = canvas.height > 0 ? pos.y / canvas.height : pos.y / 2262;

    const stroke = {
      type: 'start',
      page: pageNum,
      x: normalizedX,
      y: normalizedY,
      color: strokeColor,
      size: strokeSize,
      mode: penMode,
    };
    strokesRef.current.push(stroke);
    sendDocMessage({
      type: 'DOC_DRAW_START',
      page: pageNum,
      x: normalizedX,
      y: normalizedY,
      color: strokeColor,
      size: strokeSize,
      mode: penMode,
    });
  };

  const drawMove = (e, pageNum = 1) => {
    if (!isDrawing || !isTutorUser || interactionMode !== 'draw') return;
    if (activeDrawingPageRef.current !== pageNum) return;
    const canvas = pageCanvasRefs.current[pageNum];
    if (!canvas) return;
    const pos = getCanvasPos(e, canvas);
    if (!pos) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const lastPos = localLastPosRef.current[pageNum];
    const strokeColor = penColor;
    const strokeSize = penSize;
    const isEraser = penMode === 'eraser';

    if (lastPos) {
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(lastPos.x, lastPos.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = isEraser ? 'rgba(0,0,0,1)' : strokeColor;
      ctx.lineWidth = isEraser ? 36 : strokeSize;
      ctx.globalCompositeOperation = isEraser ? 'destination-out' : 'source-over';
      ctx.stroke();
      ctx.restore();
    }
    localLastPosRef.current[pageNum] = { x: pos.x, y: pos.y };

    const normalizedX = canvas.width > 0 ? pos.x / canvas.width : pos.x / 1600;
    const normalizedY = canvas.height > 0 ? pos.y / canvas.height : pos.y / 2262;

    const stroke = {
      type: 'move',
      page: pageNum,
      x: normalizedX,
      y: normalizedY,
      color: strokeColor,
      size: strokeSize,
      mode: penMode,
    };
    strokesRef.current.push(stroke);
    sendDocMessage({
      type: 'DOC_DRAW_MOVE',
      page: pageNum,
      x: normalizedX,
      y: normalizedY,
      color: strokeColor,
      size: strokeSize,
      mode: penMode,
    });
  };

  const endDraw = (pageNum = 1) => {
    if (!isDrawing) return;
    setIsDrawing(false);
    activeDrawingPageRef.current = null;
    localLastPosRef.current[pageNum] = null;
    strokesRef.current.push({ type: 'end', page: pageNum });
    sendDocMessage({ type: 'DOC_DRAW_END', page: pageNum });
  };

  const clearAnnotationCanvas = () => {
    clearLocalCanvas();
    sendDocMessage({ type: 'DOC_DRAW_CLEAR' });
  };

  // Resize canvas to match image dimensions
  const handleImageLoad = (e) => {
    const img = e.target;
    if (img) {
      ensurePageCanvasResolution(1, img.naturalWidth || 1600, img.naturalHeight || 1200);
    }
  };

  // Filter library notes
  const filteredLibraryNotes = libraryNotes.filter((note) => {
    const titleMatch = (note.title || note.file_name || '')
      .toLowerCase()
      .includes(librarySearch.toLowerCase());
    const chapterMatch = (note.chapter_name || note.subject_name || '')
      .toLowerCase()
      .includes(librarySearch.toLowerCase());
    const matchesSearch = titleMatch || chapterMatch;

    if (!matchesSearch) return false;
    if (libraryFilter === 'all') return true;

    const isPdf =
      note.file_url?.toLowerCase().includes('.pdf') ||
      note.file_name?.toLowerCase().endsWith('.pdf');

    if (libraryFilter === 'pdf') return isPdf;
    if (libraryFilter === 'image') return !isPdf;
    return true;
  });

  const handleClose = () => {
    shareRequestRef.current?.abort();
    sendDocMessage({ type: 'DOC_CLOSE' });
    onClose();
  };

  return (
    <div className={`relative flex-1 min-h-0 flex flex-col h-full overflow-hidden ${isDark ? 'bg-slate-950 text-white' : 'bg-slate-50 text-slate-900'}`}>
      {/* Top Document Controls Bar */}
      <div className={`min-h-[2.75rem] sm:min-h-[3rem] px-2 sm:px-4 border-b flex items-center justify-between gap-1.5 sm:gap-2 shrink-0 ${isDark ? 'bg-slate-900/95 border-slate-800' : 'bg-white/95 border-slate-200 shadow-sm'}`}>
        {/* Document Title */}
        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 overflow-hidden">
          <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500 shrink-0" />
          <span className="text-xs font-bold truncate max-w-[80px] xs:max-w-[140px] sm:max-w-[260px]" title={docState.name}>
            {docState.name || 'Shared Document'}
          </span>
          {docState.url && (
            <span className={`text-[9px] sm:text-[10px] font-semibold px-1 sm:px-1.5 py-0.2 rounded uppercase shrink-0 ${docState.type === 'pdf' ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20' : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'}`}>
              {docState.type}
            </span>
          )}
        </div>

        {/* Action Buttons: Scroll vs Draw Mode, Pen Tools, Zoom, Library, Upload */}
        <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
          {/* 1. Scroll / View vs Annotate / Draw Mode Toggle */}
          {docState.url && (
            <div className={`flex items-center gap-0.5 rounded-xl border p-0.5 ${isDark ? 'border-slate-800 bg-slate-950' : 'border-slate-200 bg-slate-100'}`}>
              <button
                onClick={() => setInteractionMode('scroll')}
                className={`p-1 xs:px-2 xs:py-1 rounded-lg text-xs font-bold transition flex items-center gap-1 ${interactionMode === 'scroll'
                  ? 'bg-emerald-600 text-white shadow-sm'
                  : 'text-slate-400 hover:text-slate-200'
                  }`}
                title="Scroll & Read Document (Full Gesture & Wheel Scrolling)"
              >
                <MousePointer className="w-3.5 h-3.5" />
                <span className="hidden xs:inline">Scroll</span>
              </button>

              {isTutorUser && (
                <button
                  onClick={() => setInteractionMode('draw')}
                  className={`p-1 xs:px-2 xs:py-1 rounded-lg text-xs font-bold transition flex items-center gap-1 ${interactionMode === 'draw'
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-slate-200'
                    }`}
                  title="Draw & Annotate Document"
                >
                  <PenTool className="w-3.5 h-3.5" />
                  <span className="hidden xs:inline">Draw</span>
                </button>
              )}
            </div>
          )}

          {/* 2. Drawing Tools Toolbar (Only shown when Tutor is in Draw mode) */}
          {isTutorUser && interactionMode === 'draw' && docState.url && (
            <div className={`flex items-center gap-1 pl-1 border-l ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
              {/* Pen Tool Button */}
              <button
                onClick={() => setPenMode('pen')}
                className={`p-1.5 rounded-xl transition ${penMode === 'pen'
                  ? 'bg-emerald-600/20 text-emerald-400 border border-emerald-500/40 shadow-sm'
                  : isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-900'
                  }`}
                title="Pen Tool"
              >
                <PenTool className="w-3.5 h-3.5" />
              </button>

              {/* Eraser Tool Button */}
              <button
                onClick={() => setPenMode('eraser')}
                className={`p-1.5 rounded-xl transition ${penMode === 'eraser'
                  ? 'bg-emerald-600/20 text-emerald-400 border border-emerald-500/40 shadow-sm'
                  : isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-900'
                  }`}
                title="Eraser Tool"
              >
                <Eraser className="w-3.5 h-3.5" />
              </button>

              {/* Color Presets */}
              {penMode === 'pen' && (
                <div className="hidden sm:flex items-center gap-1 ml-0.5">
                  {[
                    { color: '#ef4444', label: 'Red' },
                    { color: '#3b82f6', label: 'Blue' },
                    { color: '#10b981', label: 'Green' },
                    { color: '#eab308', label: 'Yellow' },
                    { color: '#ffffff', label: 'White' },
                  ].map((c) => (
                    <button
                      key={c.color}
                      onClick={() => setPenColor(c.color)}
                      style={{ backgroundColor: c.color }}
                      className={`w-4 h-4 rounded-full transition-transform ${penColor === c.color ? 'scale-125 ring-2 ring-emerald-500 ring-offset-1' : 'hover:scale-110 opacity-80'}`}
                      title={c.label}
                    />
                  ))}
                </div>
              )}


              {/* Clear All Markings */}
              <button
                onClick={clearAnnotationCanvas}
                className="p-1.5 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 rounded-xl transition ml-0.5"
                title="Clear Markings"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* 3. Zoom Controls */}
          {docState.url && (
            <div className={`hidden sm:flex items-center gap-0.5 rounded-xl border p-0.5 ${isDark ? 'border-slate-800 bg-slate-950' : 'border-slate-200 bg-slate-100'}`}>
              <button
                onClick={() => setZoom((z) => Math.max(0.6, Math.round((z - 0.15) * 100) / 100))}
                className="p-1 text-slate-400 hover:text-slate-200 rounded-lg transition"
                title="Zoom Out"
              >
                <ZoomOut className="w-3.5 h-3.5" />
              </button>
              <span className="text-[10px] font-mono font-bold w-10 text-center select-none">
                {Math.round(zoom * 100)}%
              </span>
              <button
                onClick={() => setZoom((z) => Math.min(2.5, Math.round((z + 0.15) * 100) / 100))}
                className="p-1 text-slate-400 hover:text-slate-200 rounded-lg transition"
                title="Zoom In"
              >
                <ZoomIn className="w-3.5 h-3.5" />
              </button>
              {zoom !== 1 && (
                <button
                  onClick={() => setZoom(1)}
                  className="p-1 text-slate-400 hover:text-slate-200 rounded-lg transition"
                  title="Reset Zoom"
                >
                  <RotateCcw className="w-3 h-3" />
                </button>
              )}
            </div>
          )}

          {/* 4. Browse Teacher Document Library (Tutor Only) */}
          {isTutorUser && (
            <button
              onClick={handleOpenLibrary}
              className={`px-2 sm:px-2.5 py-1 sm:py-1.5 rounded-xl border text-xs font-bold transition flex items-center gap-1.5 ${isDark ? 'border-slate-700 bg-slate-800/80 hover:bg-slate-700 text-slate-200' : 'border-slate-300 bg-slate-100 hover:bg-slate-200 text-slate-700'}`}
              title="Browse My Library (Uploaded Notes & Worksheets)"
            >
              <FolderOpen className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
              <span className="hidden sm:inline">My Documents</span>
            </button>
          )}

          {/* 5. Upload New Device File (Tutor Only) */}
          {isTutorUser && (
            <label className="cursor-pointer px-1.5 sm:px-2.5 py-1 sm:py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-xl transition flex items-center gap-1.5 shadow-sm">
              <FileUp className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Upload New</span>
              <input
                type="file"
                accept="application/pdf,image/*"
                onChange={handleFileUpload}
                className="hidden"
                disabled={isUploading}
              />
            </label>
          )}

          {/* Close Document Viewer Button */}
          <button
            onClick={handleClose}
            className={`p-1.5 rounded-xl border transition ${isDark ? 'border-slate-800 hover:bg-slate-800 text-slate-400 hover:text-slate-200' : 'border-slate-300 hover:bg-slate-200 text-slate-600'}`}
            title="Close Document Viewer"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Main Document Display & Scroll Area */}
      {shareError && <div role="alert" className="px-4 py-2 text-sm text-rose-500">{shareError}</div>}
      <div
        ref={containerRef}
        onScroll={handleContainerScroll}
        className="flex-1 min-h-0 w-full relative overflow-y-auto overflow-x-auto bg-slate-950/90 select-none"
        style={{
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {isUploading ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] h-full gap-3 text-slate-400">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            <p className="text-xs font-semibold">Uploading & sharing document with student...</p>
          </div>
        ) : !docState.url ? (
          /* Empty State */
          <div className="flex flex-col items-center justify-center max-w-md text-center p-6 sm:p-8 border-2 border-dashed border-slate-700/80 rounded-3xl mx-auto my-12">
            <div className="w-14 h-14 rounded-2xl bg-emerald-500/20 flex items-center justify-center text-emerald-400 mb-3 shadow-inner">
              <BookOpen className="w-7 h-7" />
            </div>
            <h3 className="text-base font-bold mb-1">No Document Shared</h3>
            <p className="text-xs text-slate-400 mb-5 max-w-xs">
              {isTutorUser
                ? 'Select a previously uploaded worksheet from your library or upload a new PDF/image to solve problems together in real time.'
                : 'Waiting for your tutor to share a document or worksheet...'}
            </p>
            {isTutorUser && (
              <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  onClick={handleOpenLibrary}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-xl transition flex items-center gap-2 shadow-lg shadow-emerald-600/30"
                >
                  <FolderOpen className="w-4 h-4" />
                  <span>Browse My Library</span>
                </button>

                <label className="cursor-pointer px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold rounded-xl transition flex items-center gap-2 border border-slate-700">
                  <FileUp className="w-4 h-4" />
                  <span>Upload from Device</span>
                  <input
                    type="file"
                    accept="application/pdf,image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
              </div>
            )}
          </div>
        ) : (
          /* Document + Collaborative Markup Stack */
          <div
            style={{
              width: zoom > 1 ? `${Math.round(zoom * 100)}%` : '100%',
              transform: zoom < 1 ? `scale(${zoom})` : undefined,
              transformOrigin: 'top center',
            }}
            className="w-full flex flex-col items-center py-4 space-y-4"
          >
            {/* CSS styles to ensure react-pdf canvas is strictly visible and centered */}
            <style>{`
              .react-pdf__Page__canvas {
                display: block !important;
                visibility: visible !important;
                opacity: 1 !important;
                max-width: 100% !important;
                height: auto !important;
                margin: 0 auto !important;
              }
              .react-pdf__Page {
                position: relative !important;
                display: flex !important;
                justify-content: center !important;
                background-color: transparent !important;
              }
            `}</style>

            {docState.type === 'pdf' ? (
              pdfError ? (
                /* Clean Error Card with Retry button and Direct Open link */
                <div className="flex flex-col items-center justify-center py-20 px-6 text-center text-slate-700 min-w-[280px] max-w-md bg-white rounded-2xl shadow-xl my-8">
                  <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center text-rose-500 mb-3">
                    <FileText className="w-6 h-6" />
                  </div>
                  <h3 className="text-sm font-bold text-slate-900 mb-1">Could not render PDF document</h3>
                  <p className="text-xs text-slate-500 mb-4">
                    {typeof pdfError === 'string' ? pdfError : 'The PDF document could not be rendered in this session.'}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setPdfError(false);
                        setPdfFileSource(null);
                        setRetryVersion(value => value + 1);
                      }}
                      className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-semibold transition"
                    >
                      Retry Loading
                    </button>
                  </div>
                </div>
              ) : isPdfFetching || (docState.pageManifestUrl && !displayPages.length) ? (
                <div className="flex flex-col items-center justify-center py-24 px-12 gap-3 text-slate-500 min-w-[280px] min-h-[400px] bg-white rounded-2xl shadow-xl my-8">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                  <span className="text-xs font-semibold">Preparing PDF document...</span>
                </div>
              ) : docState.pageManifestUrl ? (
                <div className="w-full flex flex-col items-center space-y-4">
                  <LiveClassPageStack
                    key={docState.pageManifestUrl}
                    pages={displayPages} currentPage={docState.currentPage} width={pageRenderWidth}
                    scrollRef={containerRef} pageRefs={pageContainerRefs} renderOverlay={renderOverlay}
                    renderPage={(page, number) => <img
                      src={liveDocumentUrl(page.image_url, liveDocumentUrl(docState.pageManifestUrl))} alt={`Page ${number}`} draggable={false}
                      decoding="async" referrerPolicy="no-referrer"
                      onError={() => setPdfError(true)}
                      className="absolute inset-0 w-full h-full object-contain select-none"
                    />}
                  />
                </div>
              ) : (
                <Document
                  file={pdfFileSource || ensureHttps(docState.url)}
                  onLoadSuccess={handleDocumentLoadSuccess}
                  onLoadError={(err) => {
                    if (err?.message === 'Worker was destroyed' || err?.message === 'Worker was terminated') return;
                    console.warn('PDF load error:', err);
                    setPdfError(true);
                  }}
                  loading={
                    <div className="flex flex-col items-center justify-center py-24 px-12 gap-3 text-slate-500 min-w-[280px] min-h-[400px] bg-white rounded-2xl shadow-xl my-8">
                      <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                      <span className="text-xs font-semibold">Loading PDF document...</span>
                    </div>
                  }
                  options={pdfOptions}
                  className="w-full flex flex-col items-center space-y-4"
                >
                  <LiveClassPageStack
                    key={docState.url}
                    pages={displayPages} currentPage={docState.currentPage} width={pageRenderWidth}
                    scrollRef={containerRef} pageRefs={pageContainerRefs} renderOverlay={renderOverlay}
                    renderPage={(page, number) => <PageErrorBoundary key={`${docState.url}-${number}`}>
                      <Page pageNumber={number} width={pageRenderWidth}
                        devicePixelRatio={Math.min(1, Math.sqrt(1_500_000 / (pageRenderWidth ** 2 * page.height / page.width)))}
                        renderTextLayer={false} renderAnnotationLayer={false}
                        onLoadSuccess={(loadedPage) => handlePageLoadSuccess(loadedPage, number)}
                      />
                    </PageErrorBoundary>}
                  />
                </Document>
              )
            ) : (
              /* Image Document */
              <div
                ref={(el) => { if (el) pageContainerRefs.current[1] = el; }}
                className="relative inline-flex flex-col items-center justify-start bg-white shadow-2xl rounded-xl overflow-hidden mx-auto max-w-full"
              >
                <img
                  src={docState.url}
                  alt={docState.name}
                  onLoad={handleImageLoad}
                  className="w-full h-full object-contain block select-none flex-1 max-h-[90vh]"
                />
                <canvas
                  ref={(el) => { if (el) pageCanvasRefs.current[1] = el; }}
                  width={1600}
                  height={1200}
                  onMouseDown={(e) => startDraw(e, 1)}
                  onMouseMove={(e) => drawMove(e, 1)}
                  onMouseUp={() => endDraw(1)}
                  onMouseLeave={() => endDraw(1)}
                  onTouchStart={(e) => startDraw(e, 1)}
                  onTouchMove={(e) => drawMove(e, 1)}
                  onTouchEnd={() => endDraw(1)}
                  className={`absolute inset-0 w-full h-full block ${
                    isTutorUser && interactionMode === 'draw'
                      ? penMode === 'eraser'
                        ? 'cursor-cell pointer-events-auto z-10'
                        : 'cursor-crosshair pointer-events-auto z-10'
                      : 'pointer-events-none z-10'
                  }`}
                  style={{
                    touchAction: interactionMode === 'draw' ? 'none' : 'auto',
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Page Navigation Bar (When multi-page document) */}
      {docState.totalPages > 1 && (
        <div className={`h-10 px-4 border-t flex items-center justify-between sm:justify-center gap-3 shrink-0 ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}>
          <button
            onClick={() => handlePageChange(docState.currentPage - 1)}
            disabled={docState.currentPage <= 1 || !isTutorUser}
            className="p-1 rounded-lg hover:bg-slate-800 disabled:opacity-30 disabled:pointer-events-none transition flex items-center gap-1 text-xs"
            title="Previous Page"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden xs:inline">Prev</span>
          </button>
          <span className="text-xs font-semibold select-none">
            Page {docState.currentPage} of {docState.totalPages}
          </span>
          <button
            onClick={() => handlePageChange(docState.currentPage + 1)}
            disabled={docState.currentPage >= docState.totalPages || !isTutorUser}
            className="p-1 rounded-lg hover:bg-slate-800 disabled:opacity-30 disabled:pointer-events-none transition flex items-center gap-1 text-xs"
            title="Next Page"
          >
            <span className="hidden xs:inline">Next</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Teacher Documents Library Modal */}
      {showLibraryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-150">
          <div className={`w-full max-w-xl max-h-[85vh] flex flex-col rounded-2xl shadow-2xl border ${isDark ? 'bg-slate-900 border-slate-800 text-white' : 'bg-white border-slate-200 text-slate-900'} overflow-hidden`}>
            {/* Modal Header */}
            <div className={`p-3.5 sm:p-4 border-b flex items-center justify-between gap-2 ${isDark ? 'border-slate-800 bg-slate-900/50' : 'border-slate-200 bg-slate-50'}`}>
              <div className="flex items-center gap-2 min-w-0">
                <FolderOpen className="w-5 h-5 text-emerald-500 shrink-0" />
                <div>
                  <h3 className="text-sm font-bold truncate">My Document Library</h3>
                  <p className="text-[11px] text-slate-400">Select any previously uploaded worksheet or class note to share</p>
                </div>
              </div>
              <button
                onClick={() => setShowLibraryModal(false)}
                className="p-1.5 rounded-xl hover:bg-slate-800 text-slate-400 hover:text-slate-200 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Filters & Search Bar */}
            <div className={`p-3 border-b flex flex-col xs:flex-row gap-2 ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
              <div className="relative flex-1">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search by title, subject or chapter..."
                  value={librarySearch}
                  onChange={(e) => setLibrarySearch(e.target.value)}
                  className={`w-full pl-8 pr-3 py-1.5 text-xs rounded-xl border outline-none ${isDark ? 'bg-slate-950 border-slate-800 text-white placeholder-slate-500' : 'bg-slate-50 border-slate-300 text-slate-900 placeholder-slate-400'}`}
                />
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {['all', 'pdf', 'image'].map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setLibraryFilter(filter)}
                    className={`px-2.5 py-1 text-xs font-semibold rounded-lg capitalize transition ${libraryFilter === filter
                      ? 'bg-emerald-600 text-white'
                      : isDark ? 'bg-slate-800 text-slate-400 hover:text-slate-200' : 'bg-slate-100 text-slate-600 hover:text-slate-900'
                      }`}
                  >
                    {filter}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes List */}
            {shareError && <div role="alert" className="px-4 py-2 text-sm text-rose-500">{shareError}</div>}
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {loadingLibrary ? (
                <div className="flex flex-col items-center justify-center py-12 text-slate-400 gap-2">
                  <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                  <span className="text-xs">Loading library documents...</span>
                </div>
              ) : filteredLibraryNotes.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-slate-400 gap-2 text-center">
                  <FileText className="w-8 h-8 text-slate-600 mb-1" />
                  <span className="text-xs font-semibold">No documents found</span>
                  <p className="text-[11px] text-slate-500 max-w-xs">
                    {librarySearch ? 'Try a different search term or filter' : 'Upload worksheets from device to view them here later'}
                  </p>
                </div>
              ) : (
                filteredLibraryNotes.map((note) => {
                  const isPdf = note.file_url?.toLowerCase().includes('.pdf') || note.file_name?.toLowerCase().endsWith('.pdf');
                  return (
                    <div
                      key={note.id || note.file_url}
                      onClick={() => handleSelectLibraryDoc(note)}
                      aria-disabled={isUploading}
                      className={`p-3 rounded-xl border transition cursor-pointer flex items-center justify-between gap-3 ${isDark
                        ? 'border-slate-800 bg-slate-950/40 hover:bg-slate-800/80 hover:border-emerald-500/50'
                        : 'border-slate-200 bg-slate-50/70 hover:bg-emerald-50/50 hover:border-emerald-300'
                        }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${isPdf ? 'bg-rose-500/10 text-rose-400' : 'bg-blue-500/10 text-blue-400'}`}>
                          {isPdf ? <FileText className="w-5 h-5" /> : <BookOpen className="w-5 h-5" />}
                        </div>
                        <div className="min-w-0">
                          <h4 className="text-xs font-bold truncate">
                            {note.title || note.file_name || 'Class Document'}
                          </h4>
                          <p className="text-[10px] text-slate-400 truncate">
                            {note.subject_name ? `${note.subject_name} • ` : ''}
                            {note.chapter_name || 'Class Resource'}
                          </p>
                        </div>
                      </div>
                      <span className="px-2 py-1 text-[10px] font-bold bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg shrink-0 transition">
                        Select
                      </span>
                    </div>
                  );
                })
              )}
            </div>

            {/* Modal Footer */}
            <div className={`p-3 border-t flex items-center justify-between text-[11px] text-slate-400 ${isDark ? 'border-slate-800 bg-slate-900/50' : 'border-slate-200 bg-slate-50'}`}>
              <span>{filteredLibraryNotes.length} document{filteredLibraryNotes.length === 1 ? '' : 's'} available</span>
              <button
                onClick={() => setShowLibraryModal(false)}
                className="px-3 py-1 text-xs font-semibold rounded-lg hover:bg-slate-800 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
